// src/Services/apiError.ts
// Helper central para extrair mensagens de erro das respostas da API de forma segura.
//
// A API retorna erros em dois formatos:
//   1. Erro de negócio:  { code, message, errors? }
//   2. Erro de validação de formulário (Symfony):
//      { code, message: "Validation Failed", errors: [{ message, cause }], children: {...} }
//
// Além disso, o axios pode falhar ANTES de receber resposta (rede, timeout, CORS),
// caso em que `error.response` é undefined — precisamos de fallback seguro.

export interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      code?: number;
      title?: string;
      message?: string;
      errors?: unknown;
      children?: Record<string, unknown>;
      // envelope usado por alguns endpoints de validação
      data?: {
        title?: string;
        message?: string;
        errors?: unknown;
        children?: Record<string, unknown>;
      };
    };
  };
  message?: string;
  code?: string;
}

/**
 * Extrai a melhor mensagem de erro disponível de forma defensiva.
 * Nunca lança — sempre retorna uma string exibível ao usuário.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Ocorreu um erro. Por favor, tente novamente.",
): string {
  const err = error as ApiErrorShape;

  // 1. Sem response = erro de rede / timeout / CORS
  if (!err?.response) {
    if (err?.code === "ECONNABORTED") {
      return "A conexão expirou. Verifique sua internet e tente novamente.";
    }
    if (err?.message === "Network Error") {
      return "Não foi possível conectar ao servidor. Verifique sua conexão.";
    }
    return err?.message || fallback;
  }

  const status = err.response.status;
  let data     = err.response.data as any;

  // Alguns endpoints (ex: form validation) embrulham em { data: {...} }.
  // Desembrulha para acessar title/children/errors no nível certo.
  if (data && typeof data === "object" && data.data && (data.data.children || data.data.errors || data.data.title)) {
    data = data.data;
  }

  // 2. Erros de validação do Symfony form → concatena as mensagens dos campos
  const validationMsg = extractValidationErrors(data);
  if (validationMsg) return validationMsg;

  // 3. Mensagem direta da API
  if (data?.message && data.message !== "Validation Failed") {
    return data.message;
  }

  // "Validation Failed" sem mensagens extraídas → texto genérico melhor
  if (data?.title === "Validation Failed" || data?.message === "Validation Failed") {
    return "Verifique os campos: há informações inválidas ou já em uso.";
  }

  // 4. Fallback por status HTTP
  if (status) return statusFallback(status, fallback);

  return fallback;
}

/**
 * Percorre a estrutura de erros de validação do Symfony e monta uma
 * lista legível "Campo: mensagem". Trata children ANINHADOS recursivamente
 * (ex: profile → children → address → children → postalCode).
 */
function extractValidationErrors(
  data?: { errors?: unknown; children?: Record<string, unknown> },
): string | null {
  if (!data) return null;

  const messages: string[] = [];

  // rótulos amigáveis para os campos conhecidos
  const fieldLabel: Record<string, string> = {
    email:      "E-mail",
    password:   "Senha",
    fullName:   "Nome completo",
    instagram:  "Instagram",
    site:       "Site",
    postalCode: "CEP",
    street:     "Endereço",
    number:     "Número",
    complement: "Complemento",
    district:   "Bairro",
    city:       "Cidade",
    state:      "Estado",
    stateShort: "UF",
    country:    "País",
    estabilishments: "Estabelecimentos",
  };

  // percorre recursivamente node.errors[] e node.children{}
  const walk = (node: any, path: string): void => {
    if (!node || typeof node !== "object") return;

    // erros deste nível
    if (Array.isArray(node.errors)) {
      for (const e of node.errors) {
        const m = typeof e === "string" ? e : e?.message;
        if (m) {
          const label = fieldLabel[path] ?? path;
          messages.push(path ? `${label}: ${m}` : m);
        }
      }
    }

    // desce nos filhos
    if (node.children && typeof node.children === "object") {
      for (const [field, child] of Object.entries(node.children)) {
        walk(child, field);
      }
    }
  };

  // raiz: errors[] direto + children{}
  if (Array.isArray(data.errors)) {
    for (const e of data.errors) {
      const m = typeof e === "string" ? e : (e as { message?: string })?.message;
      if (m) messages.push(m);
    }
  }
  if (data.children && typeof data.children === "object") {
    for (const [field, child] of Object.entries(data.children)) {
      walk(child, field);
    }
  }

  return messages.length > 0 ? messages.join("\n") : null;
}

/** Mensagens amigáveis por código HTTP quando a API não envia texto útil. */
function statusFallback(status: number, fallback: string): string {
  switch (status) {
    case 400: return "Requisição inválida. Verifique os dados enviados.";
    case 401: return "Sessão expirada. Faça login novamente.";
    case 403: return "Você não tem permissão para realizar esta ação.";
    case 404: return "Registro não encontrado.";
    case 409: return "Conflito: este registro já existe ou está em uso.";
    case 422: return "Dados inválidos. Verifique os campos e tente novamente.";
    case 429: return "Muitas requisições. Aguarde um momento e tente de novo.";
    case 500: return "Erro interno do servidor. Tente novamente mais tarde.";
    case 502:
    case 503:
    case 504: return "Servidor temporariamente indisponível. Tente novamente.";
    default:  return fallback;
  }
}